import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { Link } from "@/lib/u-router";
import { createMemo, For, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { BarChart3, Bookmark, BookOpen, FileText, Highlighter, TrendingUp } from "lucide-solid";
import { queryKeys } from "@bso/shared";
import {
  and,
  bible_bookmark,
  bible_note,
  bible_note_verse,
  bible_verse_highlight,
  eq,
  isNull,
} from "@bso/powersync";
import { createPowerSyncQuery } from "@bso/shared/solid";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { getBookNameDescriptor, idToAbbr } from "@/utils/bible-books-data";
import { BarChart, type BarChartDatum } from "@/components/ui/bar-chart";
import { EmptyIllustration } from "@/components/ui/empty-illustration";

const OLD_TESTAMENT_MAX = 39;
const TOP_BOOKS = 8;
const TOP_DEEP_LINKS = 5;

interface BookRow {
  book: number | null;
}

function tally(rows: readonly BookRow[] | null | undefined): Map<number, number> {
  const map = new Map<number, number>();
  for (const r of rows ?? []) {
    if (r.book === null || r.book === undefined) {
      continue;
    }
    map.set(r.book, (map.get(r.book) ?? 0) + 1);
  }
  return map;
}

export const analyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/analytics",
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();

  const enabled = () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId());

  const bookmarksQuery = createPowerSyncQuery<BookRow>({
    queryKey: () => queryKeys.bible.analyticsByBook("bookmarks", session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({ book: bible_bookmark.book })
        .from(bible_bookmark)
        .where(and(eq(bible_bookmark.user_id, u), isNull(bible_bookmark.deleted_at)));
    },
    enabled,
    suspense: false,
  });

  const highlightsQuery = createPowerSyncQuery<BookRow>({
    queryKey: () => queryKeys.bible.analyticsByBook("highlights", session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({ book: bible_verse_highlight.book })
        .from(bible_verse_highlight)
        .where(and(eq(bible_verse_highlight.user_id, u), isNull(bible_verse_highlight.deleted_at)));
    },
    enabled,
    suspense: false,
  });

  const notesQuery = createPowerSyncQuery<BookRow>({
    queryKey: () => queryKeys.bible.analyticsByBook("notes", session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      // Notes link to verses through bible_note_verse; ownership + tombstone
      // live on bible_note, so we join to scope to the current user.
      return dbInstance
        .select({ book: bible_note_verse.book })
        .from(bible_note_verse)
        .innerJoin(bible_note, eq(bible_note_verse.note_id, bible_note.id))
        .where(and(eq(bible_note.user_id, u), isNull(bible_note.deleted_at)));
    },
    enabled,
    suspense: false,
  });

  const bookmarksByBook = createMemo(() => tally(bookmarksQuery.data));
  const highlightsByBook = createMemo(() => tally(highlightsQuery.data));
  const notesByBook = createMemo(() => tally(notesQuery.data));

  const totalBookmarks = createMemo(() => bookmarksQuery.data?.length ?? 0);
  const totalHighlights = createMemo(() => highlightsQuery.data?.length ?? 0);
  const totalNotes = createMemo(() => notesQuery.data?.length ?? 0);

  const grandTotal = createMemo(() => totalBookmarks() + totalHighlights() + totalNotes());

  // Combined activity per book → top N books, for the headline bar chart.
  const topBooks = createMemo<BarChartDatum[]>(() => {
    const combined = new Map<number, number>();
    const add = (m: Map<number, number>) => {
      for (const [book, n] of m) {
        combined.set(book, (combined.get(book) ?? 0) + n);
      }
    };
    add(bookmarksByBook());
    add(highlightsByBook());
    add(notesByBook());

    return [...combined.entries()]
      .toSorted((a, b) => b[1] - a[1])
      .slice(0, TOP_BOOKS)
      .map(([book, value]) => ({
        key: String(book),
        label: t(getBookNameDescriptor(book)),
        value,
      }));
  });

  // Testament split, as a simple two-bar comparison.
  const testamentSplit = createMemo<BarChartDatum[]>(() => {
    let ot = 0;
    let nt = 0;
    const add = (m: Map<number, number>) => {
      for (const [book, n] of m) {
        if (book <= OLD_TESTAMENT_MAX) {
          ot += n;
        } else {
          nt += n;
        }
      }
    };
    add(bookmarksByBook());
    add(highlightsByBook());
    add(notesByBook());
    return [
      { key: "ot", label: t(msg`Old Testament`), value: ot },
      { key: "nt", label: t(msg`New Testament`), value: nt },
    ];
  });

  const isLoading = () =>
    bookmarksQuery.isPending || highlightsQuery.isPending || notesQuery.isPending;

  return (
    <div class="flex flex-col h-full w-full bg-background text-foreground overflow-hidden">
      {/* Header */}
      <div class="border-b border-border/40 bg-card/30 backdrop-blur-lg px-4 md:px-8 py-4 md:py-5 flex items-center justify-between shrink-0">
        <div class="flex items-center gap-3">
          <div class="p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
            <BarChart3 class="h-5 w-5" />
          </div>
          <div>
            <h1 class="text-xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/75 bg-clip-text text-transparent">
              {t(msg`Your progress`)}
            </h1>
            <p class="text-xs text-muted-foreground mt-0.5">
              {t(msg`A snapshot of your study activity`)}
            </p>
          </div>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-4 md:p-8">
        <div class="mx-auto max-w-3xl space-y-6 pb-24">
          {/* Summary tiles */}
          <div class="grid grid-cols-3 gap-3 md:gap-4">
            <SummaryTile
              label={t(msg`Bookmarks`)}
              value={totalBookmarks()}
              icon={Bookmark}
              colorClass="text-amber-500"
              bgClass="bg-amber-500/10"
            />
            <SummaryTile
              label={t(msg`Highlights`)}
              value={totalHighlights()}
              icon={Highlighter}
              colorClass="text-warning"
              bgClass="bg-warning/10"
            />
            <SummaryTile
              label={t(msg`Notes`)}
              value={totalNotes()}
              icon={FileText}
              colorClass="text-primary"
              bgClass="bg-primary/10"
            />
          </div>

          <Show
            when={!isLoading() && grandTotal() > 0}
            fallback={
              <Show when={!isLoading()}>
                <div class="max-w-md mx-auto py-16 text-center border border-dashed border-border/30 bg-card/10 rounded-2xl p-6 motion-safe:animate-fade-in">
                  <EmptyIllustration variant="highlights" class="mb-3 inline-flex" />
                  <h3 class="text-sm font-bold text-foreground/85">{t(msg`No activity yet`)}</h3>
                  <p class="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">
                    {t(
                      msg`As you bookmark, highlight, and add notes, your progress will appear here.`,
                    )}
                  </p>
                  <Link
                    to="/bible/$version/$bookAbbr/$chapter"
                    params={{ version: "kjv", bookAbbr: "gen", chapter: "1" }}
                    class="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                  >
                    <BookOpen class="h-3.5 w-3.5" />
                    {t(msg`Start reading`)}
                  </Link>
                </div>
              </Show>
            }
          >
            {/* Most active books */}
            <section class="rounded-2xl border border-border/35 bg-card/30 p-5 md:p-6">
              <div class="mb-4 flex items-center gap-2">
                <TrendingUp class="h-4 w-4 text-primary" />
                <h2 class="text-sm font-bold tracking-tight">{t(msg`Most active books`)}</h2>
              </div>
              <BarChart
                data={topBooks()}
                ariaLabel={t(msg`Activity by book`)}
                barClass="bg-primary"
              />
              {/* Deep-links to jump straight into the most-studied books. */}
              <div class="mt-5 flex flex-wrap gap-2 border-t border-border/20 pt-4">
                <For each={topBooks().slice(0, TOP_DEEP_LINKS)}>
                  {(b) => (
                    <Link
                      to="/bible/$version/$bookAbbr/$chapter"
                      params={{
                        version: "kjv",
                        bookAbbr: idToAbbr(Number(b.key)),
                        chapter: "1",
                      }}
                      class="inline-flex items-center gap-1.5 rounded-full border border-border/40 bg-muted/20 px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                    >
                      <BookOpen class="h-3 w-3" />
                      {b.label}
                    </Link>
                  )}
                </For>
              </div>
            </section>

            {/* Testament split */}
            <section class="rounded-2xl border border-border/35 bg-card/30 p-5 md:p-6">
              <div class="mb-4 flex items-center gap-2">
                <BarChart3 class="h-4 w-4 text-primary" />
                <h2 class="text-sm font-bold tracking-tight">{t(msg`Old vs New Testament`)}</h2>
              </div>
              <BarChart
                data={testamentSplit()}
                ariaLabel={t(msg`Activity by testament`)}
                barClass="bg-success"
              />
            </section>
          </Show>
        </div>
      </div>
    </div>
  );
}

function SummaryTile(props: {
  label: string;
  value: number;
  icon: typeof Bookmark;
  colorClass: string;
  bgClass: string;
}) {
  return (
    <div
      class={`flex flex-col items-center justify-center gap-1.5 rounded-2xl ${props.bgClass} p-4 text-center transition-transform hover:scale-[1.02]`}
    >
      <Dynamic component={props.icon} class={`h-5 w-5 ${props.colorClass}`} />
      <span class={`text-2xl font-black leading-none tracking-tight ${props.colorClass}`}>
        {props.value}
      </span>
      <span class="text-[10px] font-bold uppercase tracking-widest text-muted-foreground opacity-80">
        {props.label}
      </span>
    </div>
  );
}
